import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { useState } from "react";
import { zugang, type ApiAntwort } from "../plattform/api";
import { passkeyUnterstuetzt } from "../plattform/geraet";
import { mitCustomTokenAnmelden, reviereMerken, type GemerktesRevier } from "../plattform/sitzung";

/**
 * Passkey-Zeremonien der App (identitaeten.md §3): Optionen holen, Authenticator des Geräts befragen, Abschluss senden,
 * mit dem Custom Token bei Firebase anmelden. Jeder Fehlschlag trägt eine {@link Passkeyfehler}-Art, nach der die Ansicht
 * ihre Meldung wählt (oberflaeche.md §9, V-20); keiner endet stumm.
 */

/**
 * - `abgebrochen`: Dialog geschlossen, kein Passkey für diese App auf dem Gerät oder Bestätigung abgelehnt (`NotAllowedError`,
 *   `AbortError`; die Plattform unterscheidet das nicht)
 * - `zeitueberschreitung`: Dialog bis zum `timeout` offen bzw. Abschluss nach Ablauf der Zeremonie (`abgelaufen`)
 * - `nicht-unterstuetzt`: kein WebAuthn (alter Browser, In-App-Browser, keine sichere Verbindung), Authenticator ohne
 *   die verlangten Fähigkeiten, Adresse passt nicht zur Relying Party
 * - `vorhanden`: auf dem Gerät liegt schon ein Passkey dieses Kontos (`excludeCredentials`, `InvalidStateError`)
 * - `abgelehnt`: der Dienst hat abgewiesen (Grund in `antwort.grund`)
 * - `netz`: keine Antwort des Dienstes
 */
export type Passkeyfehler = "abgebrochen" | "zeitueberschreitung" | "nicht-unterstuetzt" | "vorhanden" | "abgelehnt" | "netz";

export interface Zeremonieergebnis {
  /** letzte Antwort des Dienstes (Optionen bzw. Abschluss) */
  antwort: ApiAntwort;
  fehler: Passkeyfehler | null;
}

type Art = "registrierung" | "anmeldung";

/** Optionen höchstens so alt verwenden; der Dienst nimmt einen Abschluss bis 5 Minuten nach den Optionen an. */
export const VORRAT_MS = 240_000;
const TIMEOUT_MS = 300_000;

const NICHT_UNTERSTUETZT_NAMEN = new Set(["NotSupportedError", "SecurityError", "ConstraintError"]);

/** Art eines Fehlers von `navigator.credentials.create/get` (über SimpleWebAuthn), nach `dauerMs` seit dem Aufruf. */
export function fehlerart(e: unknown, dauerMs: number, timeoutMs: number): Passkeyfehler {
  const name = e instanceof Error ? e.name : "";
  const ursache = e instanceof Error && e.cause instanceof Error ? e.cause.name : "";
  const namen = [name, ursache];
  if (namen.includes("InvalidStateError")) return "vorhanden";
  if (namen.some((n) => NICHT_UNTERSTUETZT_NAMEN.has(n))) return "nicht-unterstuetzt";
  if (e instanceof Error && /not supported/i.test(e.message) && !namen.includes("NotAllowedError")) return "nicht-unterstuetzt";
  if (namen.includes("NotAllowedError") && dauerMs >= timeoutMs * 0.95) return "zeitueberschreitung";
  return "abgebrochen";
}

/** Art eines Fehlschlags nach einer Antwort des Dienstes; `abgelaufen` im Abschluss ist die abgelaufene Zeremonie. */
export function antwortfehler(a: ApiAntwort, abschluss: boolean): Passkeyfehler | null {
  if (a.ok) return null;
  if (a.netzfehler) return "netz";
  if (abschluss && a.grund === "abgelaufen") return "zeitueberschreitung";
  return "abgelehnt";
}

const OHNE_ANTWORT: ApiAntwort = { ok: false, status: 0, grund: null, kennungen: {}, felder: {}, serverzeit: null, netzfehler: false };

async function abschliessen(pfad: string, zeremonie: unknown, antwort: unknown): Promise<ApiAntwort> {
  const a = await zugang(pfad, { zeremonie, antwort });
  if (a.ok && typeof a.felder["customToken"] === "string") {
    const uid = await mitCustomTokenAnmelden(a.felder["customToken"]);
    if (Array.isArray(a.felder["Reviere"])) reviereMerken(uid, a.felder["Reviere"] as GemerktesRevier[]);
  }
  return a;
}

interface Vorrat {
  schluessel: string;
  antwort: ApiAntwort;
  /** Zeitpunkt der Anfrage (Echtzeit, ms) */
  geholt: number;
}

/**
 * Eine Passkey-Zeremonie einer Ansicht. Safari bis iOS 17.3 ruft WebAuthn nur direkt aus einer Nutzergeste auf; ein
 * `fetch` davor kostet die Geste. Deshalb hält die Zeremonie Optionen vor: {@link vorbereiten} holt sie ohne Geste im
 * Voraus, und nach einem abgebrochenen Dialog bleiben die Optionen der unverbrauchten Zeremonie für den nächsten
 * Fingertipp erhalten (auch nötig, weil der Dienst einen Einmalcode schon mit den Optionen verbraucht).
 * {@link ausfuehren} muss im Handler des Fingertipps aufgerufen werden, ohne `await` davor: Mit vorgehaltenen Optionen
 * ruft sie WebAuthn noch in derselben Geste auf. Den Abschluss sendet sie erst, wenn kein Vorholen mehr läuft: Ein
 * Abschluss verbraucht z. B. die Einladung, und eine danach beantwortete Vorbereitung würde sonst abgewiesen.
 */
export class Passkeyzeremonie {
  private vorrat: Vorrat | null = null;
  private holen: { schluessel: string; fertig: Promise<void> } | null = null;

  constructor(private readonly art: Art, private readonly mitSitzung = false, private readonly echtzeit: () => number = () => Date.now()) {}

  private get optionenpfad(): string {
    return this.art === "registrierung" ? "passkey/registrierung/optionen" : "passkey/anmeldung/optionen";
  }

  private frisch(schluessel: string): Vorrat | null {
    const v = this.vorrat;
    return v && v.schluessel === schluessel && this.echtzeit() - v.geholt < VORRAT_MS ? v : null;
  }

  /** Erfüllt, sobald kein Vorholen dieser Zeremonie mehr läuft. */
  vorgeholt(): Promise<void> {
    return this.holen?.fertig ?? Promise.resolve();
  }

  /** Holt Optionen für diese Angaben im Voraus (wirkungslos; ohne Netz oder bei Abweisung bleibt nichts vorgehalten). */
  vorbereiten(angaben: Record<string, unknown>): void {
    if (!passkeyUnterstuetzt()) return;
    const schluessel = JSON.stringify(angaben);
    if (this.frisch(schluessel) || this.holen?.schluessel === schluessel) return;
    const geholt = this.echtzeit();
    const fertig = zugang(this.optionenpfad, angaben, this.mitSitzung).then((o) => {
      if (o.ok) this.vorrat = { schluessel, antwort: o, geholt };
    }, () => undefined).finally(() => {
      if (this.holen?.fertig === fertig) this.holen = null;
    });
    this.holen = { schluessel, fertig };
  }

  /** `vorAbschluss`: weitere Vorbereitungen der Ansicht (andere Zeremonien), die vor dem Abschluss beantwortet sein müssen. */
  ausfuehren(angaben: Record<string, unknown>, vorAbschluss: () => Promise<unknown> = () => Promise.resolve()): Promise<Zeremonieergebnis> {
    if (!passkeyUnterstuetzt()) return Promise.resolve({ antwort: OHNE_ANTWORT, fehler: "nicht-unterstuetzt" });
    const schluessel = JSON.stringify(angaben);
    const vorrat = this.frisch(schluessel);
    if (vorrat) return this.mitOptionen(vorrat, vorAbschluss);
    const laufend = this.holen?.schluessel === schluessel ? this.holen.fertig : Promise.resolve();
    return laufend.then(async () => {
      const nachgeholt = this.frisch(schluessel);
      if (nachgeholt) return this.mitOptionen(nachgeholt, vorAbschluss);
      const geholt = this.echtzeit();
      const o = await zugang(this.optionenpfad, angaben, this.mitSitzung);
      if (!o.ok) return { antwort: o, fehler: antwortfehler(o, false) };
      return this.mitOptionen({ schluessel, antwort: o, geholt }, vorAbschluss);
    });
  }

  private mitOptionen(v: Vorrat, vorAbschluss: () => Promise<unknown>): Promise<Zeremonieergebnis> {
    const optionen = v.antwort.felder["optionen"] as (PublicKeyCredentialCreationOptionsJSON & PublicKeyCredentialRequestOptionsJSON) | undefined;
    const timeout = typeof optionen?.timeout === "number" ? optionen.timeout : TIMEOUT_MS;
    const beginn = this.echtzeit();
    let authenticator: Promise<unknown>;
    try {
      authenticator = this.art === "registrierung"
        ? startRegistration({ optionsJSON: optionen as PublicKeyCredentialCreationOptionsJSON })
        : startAuthentication({ optionsJSON: optionen as PublicKeyCredentialRequestOptionsJSON });
    } catch (e) {
      authenticator = Promise.reject(e);
    }
    return authenticator.then(async (antwort) => {
      this.vorrat = null;
      await Promise.all([this.vorgeholt(), vorAbschluss().catch(() => undefined)]);
      const pfad = this.art === "registrierung" ? "passkey/registrierung/abschluss" : "passkey/anmeldung/abschluss";
      const a = await abschliessen(pfad, v.antwort.felder["zeremonie"], antwort);
      return { antwort: a, fehler: antwortfehler(a, true) };
    }, (e: unknown) => {
      const fehler = fehlerart(e, this.echtzeit() - beginn, timeout);
      console.warn("Passkey-Zeremonie nicht abgeschlossen", { fehler, name: e instanceof Error ? e.name : typeof e, meldung: e instanceof Error ? e.message : "" });
      this.vorrat = fehler === "abgebrochen" || fehler === "vorhanden" ? v : null;
      return { antwort: v.antwort, fehler };
    });
  }
}

/** Zeremonie einer Ansicht, über deren Lebensdauer gehalten (Vorrat, {@link Passkeyzeremonie}). */
export function usePasskeyzeremonie(art: Art, mitSitzung = false): Passkeyzeremonie {
  const [zeremonie] = useState(() => new Passkeyzeremonie(art, mitSitzung));
  return zeremonie;
}
